import { DropShadowFilter } from "@pixi/filter-drop-shadow"
import * as PIXI from "pixi.js"
import { type Idea } from "../model/types"
import { BOX_WIDTH } from "./grid"
import { type PixiScene } from "./pixi_scene"
import { renderIdeaTextToGraphic } from "./idea_text_rendering"
import { createOccupiedGridCellResolver, renderIdeaShapeToGraphic } from "./renderers"
import { defaultIdeaColourResolver, type IdeaColourResolver } from "./idea_colour"

export type DragPreviewOffset = {
  dpx: number
  dpy: number
}

type DragPreviewOrigin = {
  gx1: number
  gy1: number
}

export default class DragPreviewRenderer {
  private scene: PixiScene
  private idea_colour_resolver: () => IdeaColourResolver
  private drag_layer: PIXI.Graphics
  private gx1: number
  private gy1: number

  constructor(
    scene: PixiScene,
    idea_colour_resolver: () => IdeaColourResolver = () => defaultIdeaColourResolver,
  ) {
    this.scene = scene
    this.idea_colour_resolver = idea_colour_resolver
  }

  set_ideas_cursor(cursor: string): void {
    if (this.scene.layers.ideas) this.scene.layers.ideas.cursor = cursor
  }

  render_drag_preview(ideas: Idea[], scale: number): void {
    const { gx1, gy1 } = ideas.reduce<DragPreviewOrigin>(
      ({ gx1, gy1 }, { location: { gx, gy } }) => {
        return {
          gx1: Math.min(gx1, gx),
          gy1: Math.min(gy1, gy),
        }
      },
      { gx1: 1000, gy1: 1000 },
    )
    this.gx1 = gx1
    this.gy1 = gy1

    this.drag_layer = new PIXI.Graphics()
    this.drag_layer.filters = [
      new DropShadowFilter({
        color: 0x888888,
        blur: 0.5,
        distance: 3,
        offset: { x: 2 * scale, y: 2 * scale },
      }),
    ]

    const ideaColourResolver = this.idea_colour_resolver()
    const isGridCellOccupied = createOccupiedGridCellResolver(ideas)
    ideas.forEach((idea) => {
      renderIdeaShapeToGraphic(
        this.drag_layer,
        idea,
        gx1,
        gy1,
        ideaColourResolver,
        isGridCellOccupied,
      )
      renderIdeaTextToGraphic(this.drag_layer, idea, null, gx1, gy1, ideaColourResolver)
    })
    this.drag_layer.x = gx1 * BOX_WIDTH
    this.drag_layer.y = gy1 * BOX_WIDTH
    this.scene.layers.drag.renderable = true
    this.scene.layers.drag.addChild(this.drag_layer)
  }

  move_drag_preview({ dpx, dpy }: DragPreviewOffset): void {
    this.drag_layer.x = this.gx1 * BOX_WIDTH + dpx - 2
    this.drag_layer.y = this.gy1 * BOX_WIDTH + dpy - 2
  }

  clear_drag_preview(): void {
    this.scene.layers.drag.renderable = false
    this.scene.layers.drag.children.forEach((child) => {
      this.scene.layers.drag.removeChild(child)
      child.destroy()
    })
  }
}
